import { ApiError, ApiErrorCode } from '@shared/infrastructure/http/api-error.js';
import type {
  AnalysisRun,
  BatchEstimate,
  LaunchOptions,
  ReanalyzeOutcome,
  Video,
} from '../model.js';
import {
  applySelection,
  buildRunOptions,
  DEFAULT_ENGINE_CATALOGUE,
  type EngineCatalogueEntry,
  engineLabel,
  normalizeCatalogue,
} from './engine-rules.js';
import { estimateSummary } from './video-presentation.js';

/**
 * Re-analysing a video (GAP-03, GAP-15): what the launch dialog decides before anything is sent.
 *
 * Three of a launch's options have consequences that outlive the launch itself. This file is
 * where those are worked out, so the dialog only has to render them and a spec can pin them down:
 *
 * - **the configuration** is sent only when it changed, because sending it rewrites the video's
 *   own configuration before the run is queued, and it stays rewritten even if the launch is then
 *   refused;
 * - **promoting without coherence** empties the video's transcription when the run finishes, so it
 *   needs an acknowledgement of its own;
 *
 * A video whose run is **pinned** upstream used to warn here too: it keeps showing the pinned run
 * while the finished one rewrites its status and its transcription. Pinning was removed from the
 * back-office, so nothing here can produce that state and the warning went with it. Virtual
 * Profiler still has the pin and its legacy frontend still sets it, so a video pinned there will
 * behave that way without this dialog saying so.
 */

/** The only source this dialog launches over: a video of the dataset. */
export const VIDEO_SOURCE = 'manual_analysis';

/** Upstream's cap on a run's batch and note. Longer is refused with a 422. */
export const RUN_TEXT_MAX_LENGTH = 200;

/** Both engines, as upstream itself launches a video when nobody chose. */
export const DEFAULT_LAUNCH_ENGINES: readonly string[] = Object.freeze(['gestures', 'coherence']);

/**
 * The batch a selection is launched under when nobody named one: `bulk-yyyymmdd-hhmm`, in UTC.
 *
 * The same name upstream would give it, so a batch filtered for in the queue reads the same
 * whether the dialog or Virtual Profiler chose it. Prefilled rather than left to upstream so the
 * operator sees the label the runs will carry before launching them.
 */
export function defaultBulkBatch(now: Date): string {
  const iso = now.toISOString();

  return `bulk-${iso.slice(0, 10).replace(/-/g, '')}-${iso.slice(11, 16).replace(':', '')}`;
}

/** What the operator filled in. Strings as the inputs hold them; nothing is trimmed yet. */
export interface LaunchDraft {
  /** The engines picked by hand. The dependency rules add to these, never the dialog. */
  selected: readonly string[];
  /** A configuration picked explicitly, or null to keep each video on its own. */
  configId: number | null;
  /** A stored version of that configuration, or null for its current one. */
  configVersionId: string | null;
  batch: string;
  note: string;
  promoteOnSuccess: boolean;
}

export interface LaunchContext {
  /** The configuration the video already runs with. Null for a selection: each keeps its own. */
  currentConfigId: number | null;
  catalogue?: readonly Partial<EngineCatalogueEntry>[];
}

/**
 * Whether the draft would move the video to another configuration.
 *
 * Choosing the configuration the video already has is not a change, and must not travel: the
 * request would still be a rewrite, just one that happens to write the same value.
 */
export function changesConfig(
  draft: Pick<LaunchDraft, 'configId'>,
  context: LaunchContext,
): boolean {
  return draft.configId !== null && draft.configId !== context.currentConfigId;
}

/**
 * The request body, from the draft.
 *
 * The engines sent are what was **requested**, not the closed selection: upstream closes it again
 * and reports what it added in `engines_auto_added`, which is how the answer can say «Gestos, porque
 * lo pide Veracidad». Sending the closed list would erase that.
 */
export function launchOptionsFrom(draft: LaunchDraft, context: LaunchContext): LaunchOptions {
  const catalogue = context.catalogue ?? DEFAULT_ENGINE_CATALOGUE;
  const selection = applySelection(catalogue, VIDEO_SOURCE, draft.selected);
  const body = buildRunOptions({
    engines: selection.requested,
    configVersionId: draft.configVersionId,
    batch: draft.batch,
    note: draft.note,
    promoteOnSuccess: draft.promoteOnSuccess,
  });

  const options: LaunchOptions = {
    engines: body.engines as string[],
    promoteOnSuccess: draft.promoteOnSuccess,
  };

  if (changesConfig(draft, context)) options.analysisConfigId = draft.configId as number;
  if (typeof body.configVersionId === 'string') options.configVersionId = body.configVersionId;
  if (typeof body.batch === 'string') options.batch = body.batch;
  if (typeof body.note === 'string') options.note = body.note;

  return options;
}

/**
 * Whether the finished run will leave the video with an empty transcription.
 *
 * A promoted run rewrites the video's transcription with whatever it produced, and a run without
 * coherence produces none. On a video whose transcription somebody has been reading, that is a loss
 * nothing on the screen would otherwise announce.
 */
export function wipesTranscription(engines: readonly string[], promoteOnSuccess: boolean): boolean {
  return promoteOnSuccess && !engines.includes('coherence');
}

/**
 * Whether a launch that failed may have been queued all the same.
 *
 * Only a launch Virtual Profiler did not confirm in time: it goes on launching after the
 * back-office stops waiting. Whoever handles the failure refreshes what shows runs and does not
 * offer the same launch again — that would queue, and pay for, it twice.
 */
export function mayHaveLaunched(error: unknown): boolean {
  return error instanceof ApiError && error.code === ApiErrorCode.VP_LAUNCH_UNCONFIRMED;
}

/**
 * The estimate line of a launch confirmation, or null before anything was asked.
 *
 * One line for every launch — a re-analysis, a replay, a group run — because the decision is
 * the same: how long before confirming. A failure is said rather than left blank, which would read
 * as «nothing to wait for».
 */
export function launchEstimateLine(state: {
  pending: boolean;
  failed: boolean;
  answer: BatchEstimate | undefined;
}): string | null {
  if (state.pending) return 'Calculando…';
  if (state.failed) return 'No se ha podido estimar';

  return state.answer ? estimateSummary(state.answer) : null;
}

/** Why a text field would be refused, or null. */
export function runTextError(text: string): string | null {
  return text.trim().length > RUN_TEXT_MAX_LENGTH
    ? `Como mucho ${RUN_TEXT_MAX_LENGTH} caracteres`
    : null;
}

export type LaunchWarningCode = 'wipes_transcription' | 'rewrites_config' | 'no_promotion';

export interface LaunchWarning {
  code: LaunchWarningCode;
  /** `destructive` loses something; `warning` changes something; `info` only explains. */
  tone: 'destructive' | 'warning' | 'info';
  title: string;
  text: string;
  /** The launch stays disabled until somebody ticks that they read this one. */
  needsAcknowledgement: boolean;
}

export interface WarningInput {
  /** The engines that will actually run: the closed selection. */
  engines: readonly string[];
  promoteOnSuccess: boolean;
  configChanged: boolean;
  /** How many videos the launch is over. The wording changes from «el vídeo» to «cada vídeo». */
  targetCount: number;
}

/**
 * What the operator has to read before confirming, most serious first.
 *
 * Only the empty transcription asks for an acknowledgement. The rest change something that can be
 * changed back. An ordinary launch has nothing to warn about, and says nothing.
 */
export function launchWarnings(input: WarningInput): LaunchWarning[] {
  const many = input.targetCount > 1;
  const warnings: LaunchWarning[] = [];

  if (wipesTranscription(input.engines, input.promoteOnSuccess)) {
    warnings.push({
      code: 'wipes_transcription',
      tone: 'destructive',
      title: many
        ? 'La transcripción de cada vídeo se quedará vacía'
        : 'La transcripción del vídeo se quedará vacía',
      text: `Sin veracidad no hay transcripción, así que al terminar sustituiría la ${
        many ? 'de cada vídeo' : 'del vídeo'
      } por una vacía. Si solo quieres repetir los gestos, desactiva «Sustituir la transcripción del vídeo».`,
      needsAcknowledgement: true,
    });
  }

  if (input.configChanged) {
    warnings.push({
      code: 'rewrites_config',
      tone: 'warning',
      title: many
        ? 'Todos los vídeos pasarán a esta configuración'
        : 'El vídeo pasará a esta configuración',
      text: 'Virtual Profiler la guarda en el vídeo antes de ponerlo en cola, y se queda aunque el lanzamiento se rechace.',
      needsAcknowledgement: false,
    });
  }

  if (!input.promoteOnSuccess) {
    warnings.push({
      code: 'no_promotion',
      tone: 'info',
      title: 'La app de Virtual Profiler no cambia',
      text: 'Seguirá mostrando el análisis que tiene ahora. En el back-office, si termina bien, será la última completa del vídeo.',
      needsAcknowledgement: false,
    });
  }

  return warnings;
}

/**
 * Why this video cannot be re-analysed right now, or null.
 *
 * Only the causes this side can see. The in-flight check is scoped to runs outside any group,
 * because that is how upstream scopes it: a group re-analysis in flight does not block a launch
 * of the video on its own. The cap of runs per video cannot be seen from here, and comes back as
 * `VP_VIDEO_BUSY` when it is hit.
 */
export function launchBlocker(
  video: Pick<Video, 'id' | 'status'> | null | undefined,
  liveRuns: readonly Pick<AnalysisRun, 'sourceType' | 'sourceId' | 'groupId'>[],
): string | null {
  if (!video) return null;

  if (video.status === 'UPLOADING') return 'El vídeo todavía se está subiendo';
  if (video.status === 'ANALYSING') return 'El vídeo ya se está analizando';

  const inFlight = liveRuns.some(
    (run) =>
      run.sourceType === VIDEO_SOURCE && run.sourceId === String(video.id) && run.groupId === null,
  );

  return inFlight
    ? 'El vídeo ya tiene un análisis en marcha: espera a que termine o cancélalo en la cola'
    : null;
}

/** Who pulled an auto-added engine in, by label: «Gestos, porque lo pide Veracidad». */
function autoAddedPhrase(
  added: readonly string[],
  engines: readonly string[],
  catalogue: readonly Partial<EngineCatalogueEntry>[],
): string {
  const entries = normalizeCatalogue(catalogue);

  return added
    .map((name) => {
      const by = engines.find((other) =>
        entries.find((entry) => entry.name === other)?.requires.includes(name),
      );
      const label = engineLabel(catalogue, name);

      return by ? `${label}, porque lo pide ${engineLabel(catalogue, by)}` : label;
    })
    .join('; ');
}

/**
 * The sentence the operator reads once the launch was accepted.
 *
 * A selection says how many were refused rather than listing them: the dialog lists them, with
 * upstream's reason beside each, and a toast is the wrong place for a list.
 */
export function launchedMessage(
  outcome: ReanalyzeOutcome,
  catalogue: readonly Partial<EngineCatalogueEntry>[] = DEFAULT_ENGINE_CATALOGUE,
): string {
  if (outcome.kind === 'single') {
    const { run } = outcome;
    const place = run.position === null ? '' : ` (posición ${run.position})`;
    const added =
      run.enginesAutoAdded.length > 0
        ? `. Se añade ${autoAddedPhrase(run.enginesAutoAdded, run.engines, catalogue)}`
        : '';

    return `Análisis en cola${place}${added}`;
  }

  const { launch } = outcome;
  // «Análisis» is the same word in the singular and the plural, which spares a branch.
  const parts = [`${launch.accepted.length} análisis en cola`, `lote ${launch.batch}`];

  if (launch.rejected.length > 0) {
    parts.push(
      `${launch.rejected.length} ${launch.rejected.length === 1 ? 'rechazado' : 'rechazados'}`,
    );
  }

  return parts.join(' · ');
}
